import React, { useEffect, useState } from "react";
import { TableCell, TableRow } from "src/components/ui/table";
import { Invoice, InvoiceLineItem, SKU } from "src/redux/billing/billing.types";
import { fieldValue, formatCurrency } from "../../utils/billing-utils";
import { formatNumber, formatPercentage } from "../../utils/utils";
import BorderedInput from "src/components/form-new/bordered-input";
import { Company } from "../../types/billing.types";
import BorderedSelect from "src/components/form-new/bordered-select";
import InvoiceDrawerLineTableRowMenu from "./invoice-drawer-line-table-row-menu";
import { NotebookPen, X } from "lucide-react";
import StatusBadgeThin from "./status-badge-thin";
import { Button } from "src/components/ui/button";


interface InvoiceDrawerLineTableRowProps {
  lineItem: InvoiceLineItem;
  invoice: Invoice;
  skus: Array<SKU>;
  invoices: Array<Invoice>;
  companies: Array<Company>;
  isLocked: boolean;
  updateLineItem: (lineItemId: string, field: string, value: any) => Promise<void>;
}

const InvoiceDrawerLineTableRow: React.FC<InvoiceDrawerLineTableRowProps> = ({
  lineItem,
  invoice,
  skus,
  invoices,
  companies,
  isLocked,
  updateLineItem,
}) => {
  const [notes, setNotes] = useState(lineItem.notes);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingField, setIsLoadingField] = useState<string | null>(null);

  useEffect(() => {
    setNotes(lineItem.notes);
  }, [lineItem.notes]);

  const handleUpdateLineItem = async(field: string, value: any) => {
    if (lineItem[field as keyof InvoiceLineItem]?.toString().trim().toLowerCase() !== value.toString().trim().toLowerCase()) {
      setIsLoading(true);
      setIsLoadingField(field);
      await updateLineItem(lineItem.id, field, value);
      setIsLoading(false);
      setIsLoadingField(null);
    }
  };

  return (
    <>
      <TableRow
        key={lineItem.id}
        className={`${notes ? '' : 'border-b border-gray-200'}`}
      >
        <TableCell className={`text-gray-600 text-sm text-center justify-center items-center ${notes ? 'pt-8 pb-2' : 'py-7'}`}>
          <StatusBadgeThin
            status={lineItem.invoice_line_item_status || 'draft'}
            attention_needed={lineItem.attention_needed}
          />
        </TableCell>
        <TableCell className='text-gray-600 text-sm'>
          <BorderedSelect
            searchEnabled={true}
            disabled={isLocked || (isLoading && isLoadingField === 'sku')}
            options={skus?.map((sku) => ({ value: sku.id, label: sku.name }))}
            value={fieldValue(lineItem, 'sku')}
            onBlur={(value) => handleUpdateLineItem('sku', value.target.value)}
          />
        </TableCell>
        <TableCell className='text-gray-600 text-sm'>
          <BorderedInput
            disabled={isLocked || (isLoading && isLoadingField === 'name')}
            className={`${isLoading && isLoadingField === 'name' ? 'animate-pulse' : ''}`}
            useTooltip={true}
            label={ lineItem.platform && lineItem.channel ? lineItem.platform.toLowerCase().replace('_', ' ') + '  (' + lineItem.channel.toLowerCase().replace('_', ' ') + ')' : ''}
            value={lineItem.name || ''}
            onBlur={(value) => handleUpdateLineItem('name', value.target.value)}
          />
        </TableCell>
        <TableCell className='text-gray-600 text-sm'>
          <BorderedInput
            disabled={isLocked || (isLoading && isLoadingField === 'qty')}
            className={`${isLoading && isLoadingField === 'qty' ? 'animate-pulse' : ''}`}
            value={fieldValue(lineItem, 'qty')}
            formatDisplay={(value) => formatNumber(Number(value))}
            onBlur={(value) => handleUpdateLineItem('qty', value.target.value)}
          />
        </TableCell>
        <TableCell className={`text-sm cursor-not-allowed text-gray-600 ${isLocked ? 'opacity-50' : 'text-gray-600'}`}>
          {formatCurrency(Number(fieldValue(lineItem, 'rate')), 'USD', 2, 6)}
        </TableCell>
        <TableCell className='text-gray-600 text-sm'>
          <BorderedInput
            disabled={isLocked || (isLoading && isLoadingField === 'margin_pct')}
            className={`${isLoading && isLoadingField === 'margin_pct' ? 'animate-pulse' : ''}`}
            label={formatCurrency(Number(lineItem.margin), 'USD', 0, 0)}
            value={fieldValue(lineItem, 'margin_pct')}
            formatDisplay={(value) => formatPercentage(Number(value))}
            onBlur={(value) => handleUpdateLineItem('margin_pct', value.target.value)}
            onKeyDown={(value: React.KeyboardEvent<HTMLInputElement>) => {
              if (value.key === 'Enter') {
                updateLineItem(lineItem.id, 'margin_pct', value.currentTarget.value);
              }
            }}
          />
        </TableCell>
        <TableCell className='text-gray-600 text-sm'>
          <BorderedInput
            disabled={isLocked || (isLoading && isLoadingField === 'advertiser_spend')}
            className={`${isLoading && isLoadingField === 'advertiser_spend' ? 'animate-pulse' : ''}`}
            label={`${formatCurrency(Number(lineItem.amount_to_bill), 'USD', 0, 0)}`}
            value={fieldValue(lineItem, 'advertiser_spend')}
            formatDisplay={(value) => formatCurrency(Number(value))}
            onBlur={(value) => handleUpdateLineItem('advertiser_spend', value.target.value)}
            onKeyDown={(value: React.KeyboardEvent<HTMLInputElement>) => {
              if (value.key === 'Enter') {
                updateLineItem(lineItem.id, 'advertiser_spend_override', value.currentTarget.value);
              }
            }}
          />
        </TableCell>

        <TableCell className='text-gray-600 text-sm'>
          <InvoiceDrawerLineTableRowMenu
            lineItem={lineItem}
            invoice={invoice}
            skus={skus}
            invoices={invoices}
            companies={companies}
            isLocked={isLocked}
            setNotes={(notes) => {setNotes(notes); }}
            updateLineItem={updateLineItem}
          />
        </TableCell>
      </TableRow>
      {notes && (
        <TableRow className="border-b border-gray-200">
          <TableCell className="text-gray-600 text-sm" />
          <TableCell
            colSpan={8}
            className="p-0"
          >
            <div className="text-gray-600 bg-gray-200 p-2 rounded-sm text-sm italic flex items-center gap-2 w-full justify-between">
              <div className="flex items-center gap-2 w-full">
                <NotebookPen className='h-4 w-4' />
                <input
                  disabled={isLocked || (isLoading && isLoadingField === 'notes')}
                  type="text"
                  value={notes || ''}
                  onChange={(e) => setNotes(e.target.value)}
                  onBlur={(e) => handleUpdateLineItem('notes', e.target.value)}
                  className={`text-gray-600 bg-gray-200 text-sm italic flex items-center gap-2 w-full focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent border-b border-gray-201 ${isLoading && isLoadingField === 'notes' ? 'animate-pulse' : ''}`}
                />
              </div>
              <Button
                disabled={isLocked || (isLoading && isLoadingField === 'notes')}
                variant="ghost"
                size="sm"
                onClick={() => {setNotes(null); updateLineItem(lineItem.id, 'notes', null);}}
                className="text-gray-600 hover:text-gray-800 justify-end text-right"
              >
                <X className='h-4 w-4' />
              </Button>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default InvoiceDrawerLineTableRow;